import { Link } from "react-router-dom";
import { Rss } from "lucide-react";
import Header from "@/components/Header";
import { blogPosts, PILLARS } from "@/data/blogPosts";
import { LinkedInCarousel } from "@/components/LinkedInCarousel";
import { SEOHead } from "@/components/SEOHead";

/**
 * The articles index is a section front, not a card grid.
 *
 * Two decisions worth keeping:
 *  1. No cards. A typographic list reads as a library; a card grid reads as content
 *     marketing. This page is built to carry 30+ pieces without smelling of volume.
 *  2. The pillar name is set LARGER than the article titles beneath it. That inverts
 *     the usual blog hierarchy on purpose, so the page argues "body of work" before
 *     it argues "here is a post."
 *
 * Cluster definition lives in src/data/blogPosts.ts (PILLARS).
 */

// Dates are stored as bare "YYYY-MM-DD". Parsing that as-is gives UTC midnight, which
// renders a day early anywhere west of Greenwich. Anchor to midday to avoid it.
const fmtDate = (date: string) =>
  new Date(`${date}T12:00:00`).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const Blog = () => {
  const sorted = [...blogPosts].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const [lead, ...rest] = sorted;

  // Only render pillars that have something in them. An empty section reads as broken.
  const sections = PILLARS.map((pillar) => ({
    pillar,
    posts: rest.filter((p) => p.pillar === pillar.id),
  })).filter((s) => s.posts.length > 0);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Meshly Articles",
    description:
      "Articles on AI verification, audit, and revenue integrity from Meshly, the trust harness for the systems your business runs on.",
    url: "https://www.meshly.ai/blog",
    publisher: {
      "@type": "Organization",
      name: "Meshly",
      logo: {
        "@type": "ImageObject",
        url: "https://www.meshly.ai/meshly-favicon-blue.png",
      },
    },
    blogPost: sorted.map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      url: `https://www.meshly.ai/blog/${post.slug}`,
      author: { "@type": "Person", name: post.author },
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Articles on verification, audit, and revenue integrity | Meshly"
        description="Working notes on AI verification, audit readiness, and revenue integrity, from the team building the trust harness for the systems your business runs on."
        canonicalUrl="https://www.meshly.ai/blog"
        structuredData={structuredData}
      />
      <Header />

      <div className="mx-auto max-w-5xl px-6 pb-24">
        {/* Masthead */}
        <header className="flex flex-wrap items-end justify-between gap-8 border-b-2 border-foreground pt-14 pb-6">
          <h1 className="font-display text-5xl font-semibold leading-[0.95] tracking-[-0.03em] text-foreground md:text-6xl">
            Meshly Articles
          </h1>
          <p className="mb-1 max-w-[44ch] text-sm leading-relaxed text-muted-foreground">
            Working notes on verification, audit, and revenue integrity, from the
            people building the trust harness.
          </p>
        </header>

        {/* Lead */}
        {lead && (
          <div className="grid items-start gap-14 border-b border-border py-11 md:grid-cols-[1fr_minmax(210px,280px)]">
            <div>
              <span className="font-mono-tech mb-4 block text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                Latest
              </span>
              <h2 className="mb-4 font-display text-3xl font-semibold leading-[1.03] tracking-[-0.025em] md:text-5xl">
                <Link
                  to={`/blog/${lead.slug}`}
                  className="text-foreground transition-colors hover:text-primary"
                >
                  {lead.title}
                </Link>
              </h2>
              <p className="mb-5 max-w-[54ch] text-lg leading-relaxed text-muted-foreground">
                {lead.excerpt}
              </p>
              <Link
                to={`/blog/${lead.slug}`}
                className="font-mono-tech group mb-5 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary"
              >
                Read the article
                <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
                  &rarr;
                </span>
              </Link>
              <div className="font-mono-tech flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.07em] text-muted-foreground">
                <span className="font-semibold text-foreground">{lead.author}</span>
                <span>{fmtDate(lead.date)}</span>
              </div>
            </div>

            <aside className="border-border pt-5 md:border-l md:pt-0 md:pl-6">
              {lead.substack ? (
                <>
                  <span className="font-mono-tech block text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    Also on Substack
                  </span>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    The longer version:{" "}
                    <a
                      href={lead.substack.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline underline-offset-2"
                    >
                      {lead.substack.title}
                    </a>
                  </p>
                </>
              ) : (
                <>
                  <span className="font-mono-tech block text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    Research
                  </span>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    <Link
                      to="/report/cost-of-fragmentation"
                      className="text-primary underline underline-offset-2"
                    >
                      The Cost of Fragmentation
                    </Link>
                    , our report on what systems that disagree actually cost.
                  </p>
                </>
              )}
            </aside>
          </div>
        )}

        {/* Section fronts */}
        {sections.map(({ pillar, posts }, i) => (
          <section
            key={pillar.id}
            className="grid gap-10 border-b border-border py-14 md:grid-cols-[minmax(190px,250px)_minmax(0,1fr)] md:gap-x-0"
          >
            <div className="self-start md:sticky md:top-8 md:pr-12">
              <span className="font-mono-tech mb-3 block text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                Section {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className="mb-3 font-display text-[30px] font-semibold leading-[0.99] tracking-[-0.028em] text-foreground md:text-[42px]">
                {pillar.name}
              </h2>
              <p className="mb-3 text-sm leading-relaxed text-muted-foreground">
                {pillar.blurb}
              </p>
              <span className="font-mono-tech block border-t border-border pt-3 text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                {posts.length} {posts.length === 1 ? "article" : "articles"}
              </span>
            </div>

            {/*
              The pillar name is set larger than the article titles, which is the whole
              point of the layout, but it also made it unclear which text was a link.
              Two fixes, both here: a vertical rule so the rail reads as a header column
              rather than more content, and a persistent underline on the article titles
              so links look like links regardless of size.
            */}
            <ol className="list-none p-0 md:border-l md:border-border md:pl-12">
              {posts.map((post, j) => (
                <li
                  key={post.slug}
                  className={j === 0 ? "pb-5 pt-0.5" : "border-t border-border py-5"}
                >
                  {/*
                    The whole block is one link, and the affordance is the explicit
                    "Read the article" line rather than an underline. An underline on a
                    serif headline was too quiet to register as a link, and it added to
                    the horizontal-rule clutter the layout already carries.
                  */}
                  <Link to={`/blog/${post.slug}`} className="group block">
                    <span className="mb-1.5 block font-display text-xl font-semibold leading-[1.22] tracking-[-0.013em] text-foreground transition-colors group-hover:text-primary">
                      {post.title}
                    </span>
                    <span className="mb-3 block max-w-[62ch] text-[15px] leading-relaxed text-muted-foreground">
                      {post.excerpt}
                    </span>
                    <span className="font-mono-tech inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">
                      Read the article
                      <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
                        &rarr;
                      </span>
                    </span>
                  </Link>
                  <div className="font-mono-tech mt-3 flex flex-wrap items-center gap-4 text-[10.5px] uppercase tracking-[0.11em] text-muted-foreground">
                    <span>{fmtDate(post.date)}</span>
                    {post.substack && (
                      <span className="text-primary">Also on Substack</span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ))}

        {/* Colophon. The author photo is the one warm, human thing on an otherwise
            typographic page, so it gets real size rather than an avatar dot. */}
        <div className="grid gap-10 pt-14 md:grid-cols-[minmax(190px,250px)_minmax(0,1fr)] md:gap-x-0">
          <h3 className="font-display text-2xl font-semibold leading-tight tracking-[-0.02em] text-foreground md:pr-12">
            About this
          </h3>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8 md:border-l md:border-border md:pl-12">
            <img
              src="/team/sandra-bustos.png"
              alt="Sandra Bustos, CEO of Meshly"
              width={144}
              height={144}
              loading="lazy"
              className="h-36 w-36 shrink-0 rounded-full object-cover"
            />
            <div>
              <p className="mb-1 font-display text-xl font-semibold tracking-[-0.015em] text-foreground">
                Sandra Bustos
              </p>
              <p className="font-mono-tech mb-3 text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                Founder and CEO, Meshly
              </p>
              <p className="mb-4 max-w-[58ch] text-[15px] leading-relaxed text-muted-foreground">
                Systems are connected; the meaning isn&rsquo;t. Meshly catches when your
                systems disagree and proves what happened, whether a person or an agent
                did it, starting in finance.
              </p>
              <a
                href="/rss.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary"
              >
                <Rss className="h-4 w-4" />
                RSS
              </a>
            </div>
          </div>
        </div>
      </div>

      <LinkedInCarousel />
    </div>
  );
};

export default Blog;
